/**
 * Contexto "atuando por" — o delegado trabalhando na conta do titular
 * (docs/PLANO_DELEGACAO.md, arquitetura D).
 *
 * Store de módulo (não estado React): o interceptor do axios, os streams SSE e o
 * hash das queries leem o contexto no instante da requisição, fora de componentes.
 * Componentes assinam via `useAtuacao()` (useSyncExternalStore).
 *
 * Regra do header (fail-closed): no contexto delegado, TODA requisição à API leva
 * `X-Atuando-Por`, exceto as rotas da própria pessoa (`ROTAS_DA_PESSOA`). Nunca o
 * contrário (uma lista do que é delegável): rota esquecida sem header gravaria na
 * conta do próprio delegado enquanto a tela mostra a do titular. Com o header, a
 * rota não delegável responde 403 no backend.
 *
 * Persistência: `sessionStorage` (por aba). Sobrevive ao F5; outra aba começa na
 * própria conta.
 */
import { useSyncExternalStore } from 'react';

export const HEADER_ATUANDO_POR = 'X-Atuando-Por';

export type NivelDelegacao = 'LEITURA' | 'COLABORACAO';

export interface Atuacao {
  delegacaoId: string;
  titularId: string;
  titularNome: string;
  nivel: NivelDelegacao;
  permiteIa: boolean;
  /** Usuário real que assumiu o contexto — outro login na mesma aba o invalida. */
  atorId: string;
  /** "Visualizar como" do gestor (A8): Leitura, sem delegação gravada; `delegacaoId` é sintético. */
  supervisao?: boolean;
  /** Quando a entrada foi registrada no servidor (ms). Ele só abre o contexto com o registro das
   *  últimas 12 horas: a cada carga, passadas 6, a web registra de novo. */
  registradaEm?: number;
}

/** Depois disto, a entrada do "visualizar como" é registrada de novo (o servidor aceita 12 h). */
export const RENOVAR_VISUALIZACAO_MS = 6 * 60 * 60 * 1000;

const STORAGE_KEY = 'jurisapp:atuando';

/**
 * Rotas que são da PESSOA logada e nunca da conta em que ela atua. Prefixos do
 * path relativo à base da API (`/api/v1`).
 */
export const ROTAS_DA_PESSOA: readonly string[] = [
  '/auth/',
  '/onboarding',
  '/contato',
  // Dado de referência (sem dono): igual em qualquer conta.
  '/tribunais',
  '/subscriptions',
  '/plans',
  '/delegacoes',
  '/escritorios',
  '/ia/preferencias',
  '/ia/prompts',
  '/logs/',
  '/push',
];

function ler(): Atuacao | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Atuacao;
    return a && typeof a.titularId === 'string' && typeof a.atorId === 'string' ? a : null;
  } catch {
    return null;
  }
}

let atual: Atuacao | null = ler();
const listeners = new Set<() => void>();

export function getAtuacao(): Atuacao | null {
  return atual;
}

/** Troca o contexto. Use `useTrocarAtuacao()` nas telas — ele limpa o cache e navega. */
export function setAtuacao(valor: Atuacao | null): void {
  atual = valor;
  try {
    if (valor) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(valor));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage indisponível (modo privado restrito): vale só em memória.
  }
  listeners.forEach((l) => l());
}

export function subscribeAtuacao(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAtuacao(): Atuacao | null {
  return useSyncExternalStore(subscribeAtuacao, getAtuacao, getAtuacao);
}

/**
 * Pode mudar algo na conta em que está? Na própria, sempre; na do titular, só em
 * Colaboração. Esconde as ações que numa delegação de Leitura só dariam 403.
 */
export function usePodeColaborar(): boolean {
  const atuacao = useAtuacao();
  return !atuacao || atuacao.nivel === 'COLABORACAO';
}

/** Path relativo à base da API, para URLs absolutas (SSE) ou relativas (axios). */
function pathDaApi(url: string): string {
  const semQuery = url.split('?')[0];
  const i = semQuery.indexOf('/api/v1');
  return i >= 0 ? semQuery.slice(i + '/api/v1'.length) : semQuery;
}

export function rotaDaPessoa(url: string): boolean {
  const path = pathDaApi(url);
  return ROTAS_DA_PESSOA.some((p) => path.startsWith(p));
}

/** Headers de atuação para uma requisição montada fora do axios (SSE via fetch). */
export function headersDeAtuacao(url: string): Record<string, string> {
  if (!atual || rotaDaPessoa(url)) return {};
  return { [HEADER_ATUANDO_POR]: atual.titularId };
}

/**
 * Escopo do cache do React Query: `null` na própria conta (hash de sempre — o
 * snapshot persistido continua válido); o titular no contexto delegado.
 */
export function escopoDoCache(): string | null {
  return atual?.titularId ?? null;
}

export const PREFIXO_HASH_DELEGADO = 'atuando:';
